import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showPendingReview, setShowPendingReview] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState(null);

  useEffect(() => {
    // Don't evaluate until auth has finished loading to avoid race conditions
    if (authLoading) {
      return;
    }
    
    // Show onboarding for authenticated users who haven't completed it
    if (user && user.id && !user.onboardingCompleted) {
      setShowOnboarding(true);
      setShowPendingReview(false);
      
      // Load any saved progress from localStorage
      const savedProgress = localStorage.getItem('onboardingProgress');
      if (savedProgress) {
        try {
          setOnboardingProgress(JSON.parse(savedProgress));
        } catch (e) {
          console.error('Failed to parse onboarding progress');
        }
      }
    } 
    // Show pending review screen if onboarding is complete but dashboard not released
    // Note: dashboardReleased might be undefined for users with old localStorage data
    // In that case, the auth validation will fetch fresh data with the field
    else if (user && user.id && user.onboardingCompleted && user.dashboardReleased === false) {
      setShowOnboarding(false);
      setShowPendingReview(true);
      localStorage.removeItem('onboardingProgress');
      setOnboardingProgress(null);
    }
    // User has full access
    else {
      setShowOnboarding(false);
      setShowPendingReview(false);
      // Clear progress if onboarding is complete
      if (user?.onboardingCompleted) {
        localStorage.removeItem('onboardingProgress');
        setOnboardingProgress(null);
      }
    }
  }, [user, authLoading]);

  const closeOnboarding = () => {
    // Only allow closing if onboarding is completed
    if (user?.onboardingCompleted) {
      setShowOnboarding(false);
      localStorage.removeItem('onboardingProgress');
    }
    // If not completed, keep showing the modal
  };

  const saveProgress = (progress) => {
    setOnboardingProgress(progress);
    localStorage.setItem('onboardingProgress', JSON.stringify(progress));
  };

  return (
    <OnboardingContext.Provider value={{
      showOnboarding,
      showPendingReview,
      closeOnboarding,
      onboardingProgress,
      saveProgress,
      isOnboardingRequired: user && !user.onboardingCompleted,
      isDashboardPending: user && user.onboardingCompleted && !user.dashboardReleased
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};